'use client'

import Link from 'next/link'
import styled from 'styled-components'

import {
  type ArticleListItemDto,
  type ProductListItem,
  type SectionListItemDto,
} from '../../shared/api/generated'
import { colors } from '../../shared/constants/colors'
import {
  HomeMobileSectionItem,
  type HomeMobileSectionKind,
} from '../../shared/ui-kit/actual/HomeMobileSectionItem'
import { HomeSectionItem, type HomeSectionKind } from '../../shared/ui-kit/actual/HomeSectionItem'
import { MainHeader } from '../../shared/ui-kit/actual/headers/MainHeader'
import { breakpoints } from '../../shared/ui-kit/breakPoints'
import { shortString } from '../../shared/utils/truncate'
import { ProductsViewSection } from './ProductsViewSection'

interface MarketServerViewProps {
  articles: ArticleListItemDto[]
  products: ProductListItem[]
  productsTotalCount?: number
  sections: SectionListItemDto[]
}

const BasePage = styled.div`
  padding: 0 12px 60px;
  width: clamp(${breakpoints.mobileMin}, 100%, ${breakpoints.desktopMax});
  margin: 0 auto;
  @media (min-width: ${breakpoints.tabletMin}) {
    padding: 0 36px 60px;
    overflow: auto;
  }
`

const MarketWrapper = styled.main`
  box-sizing: border-box;
  padding: 22px 24px 30px;
  width: 100%;
  max-width: 1280px;
  margin-inline: auto;

  @media (max-width: ${breakpoints.tabletMax}) {
    padding: 16px 0 30px;
  }
`

const HomeContent = styled.div`
  display: flex;
  align-items: flex-start;
  flex-flow: row wrap;
  gap: 40px;
`

const CategoriesSection = styled.section`
  flex: 0 1 806px;
  min-width: 0;

  @media (max-width: ${breakpoints.tabletMax}) {
    flex-basis: 100%;
  }
`

const MarketTitle = styled.h2`
  font-size: 24px;
  line-height: 1.2;
  font-weight: 700;
  margin: 8px 0 32px;
  color: ${colors.black};
`

const ArticlesSection = styled.section`
  flex: 1 1 360px;
  min-width: min(100%, 340px);
`

const DesktopCategories = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 8px 7px;
  width: 806px;
  height: 344px;

  @media (max-width: ${breakpoints.tabletMax}) {
    display: none;
  }
`

const DesktopAllCategoriesLink = styled(Link)`
  position: relative;
  flex: 0 0 122px;
  width: 122px;
  height: 168px;
  padding: 12px 19px;
  overflow: hidden;
  border-radius: 12px;
  background: #f8f8fa;
  color: ${colors.black};
  font-size: 14px;
  font-weight: 700;
  line-height: 1.5;
  text-decoration: none;

  &::after {
    content: '';
    position: absolute;
    left: -4px;
    top: 53px;
    width: 162px;
    height: 162px;
    border-radius: 94px;
    background: ${colors.primary};
    transform: rotate(-10.74deg);
  }

  img {
    position: absolute;
    z-index: 1;
    left: 56px;
    top: 108px;
    width: 47px;
    height: 46px;
  }
`

const MobileCategories = styled.div`
  display: none;

  @media (max-width: ${breakpoints.tabletMax}) {
    display: block;
    width: calc(100% + 12px);
    overflow-x: auto;
    padding: 0 12px 8px 20px;
    touch-action: pan-x;
    overscroll-behavior-inline: contain;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    @media (min-width: ${breakpoints.tabletMin}) {
      width: calc(100% + 36px);
      padding-right: 36px;
      padding-left: 0;
    }
  }
`

const MobileCategoriesTrack = styled.div`
  display: flex;
  align-content: flex-start;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 8px 7px;
  width: 1231px;
  height: 150px;
`

const MobileAllCategoriesLink = styled(Link)`
  position: relative;
  flex: 0 0 67px;
  width: 67px;
  height: 71px;
  padding: 6px;
  overflow: hidden;
  border-radius: 12px;
  background: rgba(40, 208, 208, 0.33);
  color: ${colors.black};
  font-size: 11px;
  font-weight: 700;
  line-height: 1.5;
  text-decoration: none;

  &::after {
    content: '';
    position: absolute;
    left: 12px;
    top: 25px;
    width: 59px;
    height: 59px;
    border-radius: 94px;
    background: ${colors.primary};
    transform: rotate(-10.74deg);
  }

  img {
    position: absolute;
    z-index: 1;
    left: 33px;
    top: 41px;
    width: 25px;
    height: 25px;
  }
`

const UsefulGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ArticleLink = styled(Link)`
  text-decoration: none;
  display: flex;
  gap: 4px;
  justify-content: flex-start;
  align-items: flex-start;
  flex-direction: row;
  flex-wrap: wrap;
  max-width: 440px;
  min-width: min(100%, 340px);
`

const ArticleCover = styled.img`
  object-fit: cover;
  border-radius: 18px;
  width: 130px;
  height: 130px;
`

const ArticlePlaceholder = styled.div`
  width: 130px;
  height: 130px;
  border-radius: 18px;
  background: #f5f5f5;
`

const ArticleTitle = styled.h2`
  font-size: 18px;
  margin: 0;
  font-weight: 700;
  max-width: 260px;
  color: #272728;
`

const ArticleStats = styled.div`
  margin-top: 4px;
  gap: 12px;
  justify-content: flex-start;
  display: flex;
  align-items: center;
`

const StatItem = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
  span {
    font-size: 11px;
    color: #6e6e70;
  }
`

const StatIcon = styled.img`
  width: 15px;
  height: 15px;
`

const BlogAllLink = styled(Link)`
  margin-top: 28px;
  width: min(100%, 440px);
  display: flex;
  justify-content: center;
  text-decoration: none;
  background-color: ${colors.primary};
  color: ${colors.white};
  font-size: 16px;
  font-weight: 600;
  padding: 18px 36px;
  border-radius: 10px;
`

const BottomMenu = styled.nav`
  position: fixed;
  bottom: -1px;
  left: 0px;
  margin: 0;
  border-top: 1px solid ${colors.lightGray};
  background-color: white;
  width: 100vw;
  border-radius: 30px 30px 0px 0px;
  z-index: 2000;
  box-shadow: 0px -5px 60px 0px rgba(0, 0, 0, 0.05);
  @media (min-width: ${breakpoints.desktopMin}) {
    display: none;
  }
`

const BottomMenuInner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  padding: 19px 37px 20px 37px;
`

const BottomItem = styled(Link)<{ $active?: boolean }>`
  display: flex;
  flex-direction: column;
  height: 40px;
  width: 40px;
  row-gap: 4px;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  img {
    width: 20px;
    height: 20px;
  }
  span {
    color: ${({ $active }) => ($active ? colors.primary : colors.grey)};
    font-size: 10px;
    font-weight: 600;
    line-height: 150%;
    margin: 0;
  }
`

const desktopSections: Array<{
  displayName?: string
  kind: HomeSectionKind
  name: string
  width?: number
}> = [
  { kind: 'tools', name: 'Инструменты' },
  { displayName: 'Авто', kind: 'car', name: 'Автомобили' },
  { kind: 'clothes', name: 'Одежда' },
  { kind: 'tech', name: 'Техника', width: 129 },
  { kind: 'realEstate', name: 'Недвижимость', width: 264 },
  { kind: 'people', name: 'Люди' },
  { kind: 'animals', name: 'Животные' },
  { displayName: 'Для дома\nи дачи', kind: 'home', name: 'Для дома и дачи', width: 129 },
  { displayName: 'Цифровая\nтехника', kind: 'digital', name: 'Цифровая техника' },
  { displayName: 'Специальная\nтехника', kind: 'special', name: 'Спец. техника', width: 135 },
]

const mobileSections: Array<{
  displayName?: string
  kind: HomeMobileSectionKind
  name: string
}> = [
  { kind: 'tools', name: 'Инструменты' },
  { displayName: 'Авто', kind: 'car', name: 'Автомобили' },
  { kind: 'clothes', name: 'Одежда' },
  { kind: 'realEstate', name: 'Недвижимость' },
  { kind: 'digital', name: 'Цифровая техника' },
  { displayName: 'Спец. техника', kind: 'special', name: 'Спец. техника' },
  { kind: 'tech', name: 'Техника' },
  {
    displayName: 'Водный транспорт\nи авиация',
    kind: 'water',
    name: 'Водный транспорт и авиация',
  },
  { kind: 'people', name: 'Люди' },
  { kind: 'animals', name: 'Животные' },
  { kind: 'home', name: 'Для дома и дачи' },
  { displayName: 'Здоровье', kind: 'health', name: 'Здоровье и медицина' },
  { kind: 'accessories', name: 'Аксессуары' },
  { kind: 'business', name: 'Для бизнеса' },
  { kind: 'creativity', name: 'Творчество' },
  { kind: 'children', name: 'Товары для детей' },
  { displayName: 'Путешествие', kind: 'travel', name: 'Путешествия' },
  { kind: 'sport', name: 'Спорт' },
  { displayName: 'Для фотосессии', kind: 'photo', name: 'Для фотосессий' },
  { kind: 'entertainment', name: 'Развлечения' },
  { kind: 'other', name: 'Другое' },
]

export function MarketServerView({
  articles,
  products,
  productsTotalCount,
  sections,
}: MarketServerViewProps) {
  const sectionsByName = new Map(sections.map(section => [section.name, section]))

  return (
    <BasePage>
      <MainHeader />

      <MarketWrapper>
        <HomeContent>
          <CategoriesSection>
            <DesktopCategories>
              {desktopSections.map(item => {
                const section = sectionsByName.get(item.name)

                if (!section) return null

                return (
                  <HomeSectionItem
                    key={item.name}
                    kind={item.kind}
                    title={item.displayName || item.name}
                    to={`/sections/${section.id}`}
                    width={item.width}
                  />
                )
              })}
              <DesktopAllCategoriesLink href={'/sections'}>
                Все
                <br />
                категории
                <img alt={''} src={'/images/home-sections/arrow.svg'} />
              </DesktopAllCategoriesLink>
            </DesktopCategories>

            <MobileCategories>
              <MobileCategoriesTrack>
                <MobileAllCategoriesLink href={'/sections'}>
                  Все
                  <img alt={''} src={'/images/home-sections/mobile/arrow.svg'} />
                </MobileAllCategoriesLink>
                {mobileSections.map(item => {
                  const section = sectionsByName.get(item.name)

                  if (!section) return null

                  return (
                    <HomeMobileSectionItem
                      key={item.name}
                      kind={item.kind}
                      title={item.displayName || item.name}
                      to={`/sections/${section.id}`}
                    />
                  )
                })}
              </MobileCategoriesTrack>
            </MobileCategories>
          </CategoriesSection>

          <ArticlesSection>
            <MarketTitle>Статьи</MarketTitle>

            <UsefulGrid>
              {articles.slice(0, 2).map(article => (
                <ArticleLink href={`/article/${article.id}`} key={article.id}>
                  {article.imageUrl ? (
                    <ArticleCover alt={'cover'} src={article.imageUrl} />
                  ) : (
                    <ArticlePlaceholder />
                  )}
                  <div>
                    <ArticleTitle>
                      {shortString(article.title || `Статья ${article.id}`, 50)}
                    </ArticleTitle>
                    <ArticleStats>
                      <StatItem>
                        <StatIcon src={'/images/interface/views-eye.svg'} />
                        <span>{article.viewsCount || 0}</span>
                      </StatItem>
                      <StatItem>
                        <StatIcon src={'/images/interface/comment.svg'} />
                        <span>{article.commentsCount || 0}</span>
                      </StatItem>
                      <StatItem>
                        <StatIcon src={'/images/interface/share.svg'} />
                        <span>{article.sharesCount || 0}</span>
                      </StatItem>
                    </ArticleStats>
                  </div>
                </ArticleLink>
              ))}
            </UsefulGrid>
            <BlogAllLink href={'/blog'}>Читать больше</BlogAllLink>
          </ArticlesSection>
        </HomeContent>

        <ProductsViewSection initialProducts={products} totalCount={productsTotalCount} />
      </MarketWrapper>

      <BottomMenu aria-label={'Нижнее меню'}>
        <BottomMenuInner>
          <BottomItem href={'/orders'}>
            <img alt={''} src={'/images/bottom-menu/cart-gray.svg'} />
            <span>Заказы</span>
          </BottomItem>
          <BottomItem href={'/messages'}>
            <img alt={''} src={'/images/bottom-menu/chat-gray.svg'} />
            <span>Чат</span>
          </BottomItem>
          <BottomItem href={'/blog'}>
            <img alt={''} src={'/images/bottom-menu/blog-gray.svg'} />
            <span>Блог</span>
          </BottomItem>
          <BottomItem $active href={'/'}>
            <img alt={''} src={'/images/bottom-menu/main-light.svg'} />
            <span>Главная</span>
          </BottomItem>
          <BottomItem href={'/profile'}>
            <img alt={''} src={'/images/bottom-menu/profile-gray.svg'} />
            <span>Профиль</span>
          </BottomItem>
        </BottomMenuInner>
      </BottomMenu>
    </BasePage>
  )
}
